// Organizations hook - manages organization data fetching and state
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useListOrganizationsQuery } from '@/store/api/admin/organizationsApi';
import { IOrganizationRowData } from '@/types/admin/organizations';
import { useDataTableState } from '@/contexts/admin/DataTableContext';
import { applyTextFilter, applyDateFilter, compareValues } from '@/utils/admin/filterUtils';

export const useOrganizations = () => {
  // Get filter/sort/pagination from DataTable context
  const table = useDataTableState('organizations');
  const filterModel = table.filterModel;
  const sortModel = table.sortModel;
  const searchQuery = table.searchQuery;
  
  // Fetch organizations list
  const {
    data: organizationsResponse,
    isLoading,
    error,
    refetch,
  } = useListOrganizationsQuery();

  // Transform API response to table rows (keep full organization objects for access to all data)
  const tableRows = useMemo(() => {
    if (!organizationsResponse?.response?.rows) {
      return [];
    }

    return organizationsResponse.response.rows;
  }, [organizationsResponse]);

  // Transform to row data for display
  const tableRowsData = useMemo((): IOrganizationRowData[] => {
    return tableRows.map((org) => ({
      id: org.id,
      name: org.name,
      domain: org.domain,
      createdAt: org.createdAt,
    }));
  }, [tableRows]);

  // Apply client-side filtering and sorting (matching pattern from other hooks)
  const filteredAndSortedRows = useMemo(() => {
    let rows = [...tableRowsData];

    // Apply search filter
    if (searchQuery.trim()) {
      const lowerQuery = searchQuery.toLowerCase().trim();
      rows = rows.filter((row) => {
        return (
          row.name.toLowerCase().includes(lowerQuery) ||
          row.domain.toLowerCase().includes(lowerQuery)
        );
      });
    }

    // Apply column filters using shared utilities
    filterModel.items.forEach(filter => {
      if (!filter.value) return;
      
      switch (filter.field) {
        case 'name':
          rows = rows.filter(row => applyTextFilter(row.name, filter.value, filter.operator));
          break;
          
        case 'domain':
          rows = rows.filter(row => applyTextFilter(row.domain, filter.value, filter.operator));
          break;
          
        case 'createdAt':
          rows = rows.filter(row => applyDateFilter(row.createdAt, filter.value, filter.operator));
          break;
      }
    });

    // Apply sorting using shared utility
    if (sortModel.length > 0) {
      const sort = sortModel[0];
      rows.sort((a, b) => {
        const aVal = a[sort.field as keyof IOrganizationRowData];
        const bVal = b[sort.field as keyof IOrganizationRowData];
        return compareValues(aVal, bVal, sort.sort);
      });
    }

    return rows;
  }, [tableRowsData, searchQuery, filterModel, sortModel]);

  // Prepare data for table
  const tableData = useMemo(() => ({
    rows: filteredAndSortedRows,
    count: filteredAndSortedRows.length,
  }), [filteredAndSortedRows]);

  // Enhanced error object (matching admin pattern)
  const enhancedError = useMemo(() => {
    if (!error) return null;

    return {
      originalError: error,
      userFriendlyMessage: 'Failed to load organizations. Please try again.',
      canRetry: true,
    };
  }, [error]);

  const updateSearchQuery = useCallback((query: string) => {
    table.setSearch(query);
  }, [table]);

  const refresh = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    data: tableData,
    tableRows, // Full organization objects for data access
    tableRowsData: filteredAndSortedRows, // Filtered row data for table display
    isLoading,
    error,
    enhancedError,
    searchQuery,
    updateSearchQuery,
    refresh,
  };
};

